import { X, Calculator, DollarSign, Shield, FileText } from "lucide-react";
import { formatNum } from "@/lib/constants";

// Estimaciones reales basadas en IMED (reds_comercial.com_zimed_report)
// Copago P25/P50/P75 por tipo de prestación × previsión
const ESTIMACIONES = {
  "Hemograma": {
    FONASA: { precio: 1442, bono: 457, seguro: 383, p25: 970, p50: 970, p75: 1000 },
    "ISAPRE Colmena": { precio: 1766, bono: 1132, seguro: 307, p25: 172, p50: 359, p75: 601 },
    "ISAPRE Banmédica": { precio: 1889, bono: 1039, seguro: 366, p25: 222, p50: 514, p75: 867 },
    "ISAPRE Cruz Blanca": { precio: 1738, bono: 1111, seguro: 284, p25: 193, p50: 355, p75: 575 },
    DEFAULT: { precio: 1700, bono: 900, seguro: 300, p25: 200, p50: 450, p75: 800 },
  },
  "Perfil Bioquímico": {
    FONASA: { precio: 2251, bono: 709, seguro: 526, p25: 1480, p50: 1480, p75: 1500 },
    "ISAPRE Colmena": { precio: 2912, bono: 1974, seguro: 403, p25: 257, p50: 546, p75: 871 },
    "ISAPRE Banmédica": { precio: 3820, bono: 2257, seguro: 680, p25: 370, p50: 842, p75: 1437 },
    DEFAULT: { precio: 2800, bono: 1500, seguro: 450, p25: 350, p50: 750, p75: 1200 },
  },
  "Perfil Lipídico": {
    FONASA: { precio: 5078, bono: 1592, seguro: 1503, p25: 3420, p50: 3420, p75: 3480 },
    "ISAPRE Colmena": { precio: 6267, bono: 4353, seguro: 984, p25: 523, p50: 1094, p75: 1777 },
    "ISAPRE Banmédica": { precio: 7362, bono: 4622, seguro: 1140, p25: 647, p50: 1319, p75: 1990 },
    DEFAULT: { precio: 6000, bono: 3500, seguro: 1000, p25: 600, p50: 1200, p75: 2000 },
  },
  "Radiografía": {
    FONASA: { precio: 8486, bono: 2661, seguro: 2512, p25: 3250, p50: 5770, p75: 5870 },
    "ISAPRE Colmena": { precio: 10332, bono: 7280, seguro: 1534, p25: 835, p50: 1693, p75: 2606 },
    "ISAPRE Banmédica": { precio: 10979, bono: 7017, seguro: 1922, p25: 983, p50: 2184, p75: 3332 },
    DEFAULT: { precio: 9500, bono: 5500, seguro: 1800, p25: 900, p50: 2000, p75: 3000 },
  },
  "Ecografía": {
    FONASA: { precio: 8679, bono: 2317, seguro: 2024, p25: 3268, p50: 5020, p75: 5100 },
    "ISAPRE Colmena": { precio: 9042, bono: 6365, seguro: 1275, p25: 755, p50: 1510, p75: 2264 },
    "ISAPRE Banmédica": { precio: 9983, bono: 6313, seguro: 1554, p25: 854, p50: 1897, p75: 2895 },
    DEFAULT: { precio: 9000, bono: 5000, seguro: 1500, p25: 800, p50: 1700, p75: 2500 },
  },
  "Consulta": {
    FONASA: { precio: 1465, bono: 461, seguro: 344, p25: 970, p50: 970, p75: 1000 },
    "ISAPRE Colmena": { precio: 1762, bono: 1138, seguro: 292, p25: 172, p50: 338, p75: 601 },
    "ISAPRE Banmédica": { precio: 1911, bono: 1052, seguro: 345, p25: 222, p50: 506, p75: 867 },
    DEFAULT: { precio: 1700, bono: 900, seguro: 300, p25: 200, p50: 450, p75: 800 },
  },
};

function detectarPrestacion(descripcion) {
  const d = (descripcion || "").toLowerCase();
  if (d.includes("hemograma")) return "Hemograma";
  if (d.includes("perfil bioquímico") || d.includes("bioquimico")) return "Perfil Bioquímico";
  if (d.includes("perfil lipídico") || d.includes("lipidico") || d.includes("hba1c")) return "Perfil Lipídico";
  if (d.includes("radiograf") || d.includes("rx") || d.includes("tórax")) return "Radiografía";
  if (d.includes("ecograf") || d.includes("abdominal")) return "Ecografía";
  if (d.includes("consulta") || d.includes("med familiar") || d.includes("oftalmol")) return "Consulta";
  return null;
}

function detectarPrevision(prevision, convenios) {
  const p = (prevision || "").toLowerCase();
  if (convenios?.length > 0) {
    const conv = convenios[0].convenio?.toLowerCase() || "";
    if (conv.includes("colmena")) return "ISAPRE Colmena";
    if (conv.includes("banmédica") || conv.includes("banmedica")) return "ISAPRE Banmédica";
    if (conv.includes("cruz blanca")) return "ISAPRE Cruz Blanca";
  }
  if (p.includes("colmena")) return "ISAPRE Colmena";
  if (p.includes("banmédica") || p.includes("banmedica")) return "ISAPRE Banmédica";
  if (p.includes("cruz blanca")) return "ISAPRE Cruz Blanca";
  if (p.includes("fonasa")) return "FONASA";
  return "DEFAULT";
}

function CostBar({ label, p25, p50, p75 }) {
  const max = p75 * 1.3;
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="text-slate-600">{label}</span>
        <span className="font-bold text-slate-900 tabular-nums">${formatNum(p50)}</span>
      </div>
      <div className="h-6 bg-slate-100 rounded-lg relative overflow-hidden">
        <div className="absolute top-0 bottom-0 rounded-lg bg-violet-200" style={{ left: `${(p25/max)*100}%`, width: `${((p75-p25)/max)*100}%` }} />
        <div className="absolute top-0 bottom-0 w-0.5 bg-violet-600" style={{ left: `${(p50/max)*100}%` }} />
        <div className="absolute top-1 text-[9px] font-semibold text-slate-500 tabular-nums" style={{ left: `${(p25/max)*100}%` }}>P25: ${formatNum(p25)}</div>
        <div className="absolute bottom-1 text-[9px] font-semibold text-slate-500 tabular-nums" style={{ left: `${Math.min((p75/max)*100, 85)}%` }}>P75: ${formatNum(p75)}</div>
      </div>
    </div>
  );
}

export default function EstimadorDrawer({ eventos, paciente, onClose }) {
  if (!paciente) return null;

  const prevKey = detectarPrevision(paciente.prevision, paciente.convenios);
  const prevLabel = prevKey === "DEFAULT" ? (paciente.prevision || "Sin información") : prevKey;

  // Find estimable events from the patient's history
  const prestacionesEstimadas = [];
  const seen = new Set();
  for (const e of (eventos || [])) {
    const tipo = detectarPrestacion(e.descripcion);
    if (tipo && !seen.has(tipo)) {
      seen.add(tipo);
      const data = ESTIMACIONES[tipo];
      if (data) {
        const est = data[prevKey] || data.DEFAULT;
        prestacionesEstimadas.push({ tipo, ...est, descripcion: e.descripcion, fecha: e.fecha });
      }
    }
  }

  const totalP50 = prestacionesEstimadas.reduce((s, p) => s + p.p50, 0);

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="estimador-drawer">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full max-w-lg bg-white shadow-2xl overflow-y-auto animate-in slide-in-from-right duration-300">
        {/* Header */}
        <div className="sticky top-0 z-10 p-5 border-b border-slate-200 bg-white/95 backdrop-blur">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-violet-500 flex items-center justify-center text-white">
                <Calculator className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">Estimación de Costos</h3>
                <p className="text-xs text-slate-500">Basado en datos reales IMED · {prevLabel}</p>
              </div>
            </div>
            <button onClick={onClose} className="h-8 w-8 rounded-lg hover:bg-slate-100 flex items-center justify-center transition" aria-label="Cerrar panel">
              <X className="w-4 h-4 text-slate-500" />
            </button>
          </div>
        </div>

        <div className="p-5 space-y-6">
          {/* Patient info */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex items-center gap-2 text-slate-500 mb-1">
                <Shield className="w-3.5 h-3.5" />
                <span className="text-[10px] font-semibold uppercase tracking-wider">Previsión</span>
              </div>
              <div className="text-sm font-bold text-slate-900">{prevLabel}</div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex items-center gap-2 text-slate-500 mb-1">
                <FileText className="w-3.5 h-3.5" />
                <span className="text-[10px] font-semibold uppercase tracking-wider">Convenio</span>
              </div>
              <div className="text-sm font-bold text-slate-900 truncate">
                {paciente.convenios?.[0]?.convenio || paciente.prevision || "—"}
              </div>
            </div>
          </div>

          {/* Total estimate */}
          <div className="rounded-xl border border-violet-200 bg-violet-50 p-5">
            <div className="text-xs font-semibold text-violet-700 uppercase tracking-wider">Copago estimado total (P50)</div>
            <div className="text-3xl font-bold text-slate-900 mt-1 tabular-nums">${formatNum(totalP50)}</div>
            <div className="text-xs text-slate-500 mt-1">Suma de {prestacionesEstimadas.length} prestaciones detectadas</div>
          </div>

          {/* Per-prestacion breakdown */}
          {prestacionesEstimadas.length > 0 ? (
            <div className="space-y-5">
              {prestacionesEstimadas.map((p) => (
                <div key={p.tipo} className="rounded-xl border border-slate-200 p-4 space-y-3">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{p.tipo}</div>
                    <div className="text-xs text-slate-500">{p.descripcion}</div>
                  </div>

                  {/* Desglose visual */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-slate-500">Precio prestación</span>
                      <span className="font-semibold text-slate-900 tabular-nums">${formatNum(p.precio)}</span>
                    </div>
                    <div className="h-3 rounded-full bg-slate-100 overflow-hidden flex">
                      <div className="h-full bg-blue-400" style={{ width: `${(p.bono / p.precio) * 100}%` }} title="Bono ISAPRE" />
                      <div className="h-full bg-teal-400" style={{ width: `${(p.seguro / p.precio) * 100}%` }} title="Seguro complementario" />
                      <div className="h-full bg-rose-400" style={{ width: `${(p.p50 / p.precio) * 100}%` }} title="Copago" />
                    </div>
                    <div className="flex items-center gap-4 text-[10px]">
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-400" /> ISAPRE: ${formatNum(p.bono)}</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-teal-400" /> Seguro: ${formatNum(p.seguro)}</span>
                      <span className="flex items-center gap-1 font-bold"><span className="w-2 h-2 rounded-full bg-rose-400" /> Copago: ${formatNum(p.p50)}</span>
                    </div>
                  </div>

                  {/* Range bar */}
                  <CostBar label="Rango de copago" p25={p.p25} p50={p.p50} p75={p.p75} />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-sm text-slate-400">
              No se detectaron prestaciones estimables en los eventos del paciente.
            </div>
          )}

          {/* Methodology */}
          <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 space-y-2">
            <div className="text-xs font-semibold text-blue-700 uppercase tracking-wider flex items-center gap-2">
              <DollarSign className="w-3.5 h-3.5" /> Metodología
            </div>
            <p className="text-sm text-slate-700">
              <strong>Fuente:</strong> <code className="text-xs bg-slate-100 px-1 rounded">reds_comercial.com_zimed_report</code> — 1.2M+ bonos/mes.
            </p>
            <p className="text-sm text-slate-700">
              <strong>Cálculo:</strong> Percentiles P25/P50/P75 de copago real para pacientes con la misma previsión ({prevLabel}) en los últimos 4 meses.
            </p>
            <p className="text-sm text-slate-700">
              <strong>Desglose:</strong> Precio = Bono ISAPRE + Seguro Complementario + Copago paciente.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
